import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, authAxios } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { Bell, Check, CheckCheck, Loader2, Wifi, WifiOff } from 'lucide-react';

const API = '/api/v1';

/**
 * Backend often stores link like `/bookings/<uuid>` but frontend only has `/bookings`.
 * Resolve a real in-app path (+ optional query) from link / type / extra_data.
 */
function resolveNotificationTarget(notif) {
  const extra = notif?.extra_data || notif?.metadata || {};
  const bookingId =
    extra.booking_id ||
    (typeof notif?.link === 'string' && notif.link.match(/\/bookings\/([^/?#]+)/)?.[1]) ||
    null;

  const bookingTypes = [
    'booking_created',
    'booking_accepted',
    'booking_rejected',
    'booking_completed',
    'booking_cancelled',
    'service_started',
    'payout_done',
  ];

  if (bookingId || bookingTypes.includes(notif?.type)) {
    return bookingId ? `/bookings?booking=${encodeURIComponent(bookingId)}` : '/bookings';
  }

  if (['membership_activated', 'membership_expiry'].includes(notif?.type)) {
    return '/membership';
  }

  if (['credit_purchased', 'credit_removed', 'payment_success', 'payment_reminder'].includes(notif?.type)) {
    return '/services';
  }

  if (notif?.type === 'system' && extra.source === 'push_campaign') {
    const deep = notif.link || extra.deep_link || extra.image_url;
    if (deep && typeof deep === 'string') {
      if (deep.startsWith('/') && !deep.startsWith('//')) return deep;
      if (deep.includes('/bookings/')) {
        const id = deep.match(/\/bookings\/([^/?#]+)/)?.[1];
        return id ? `/bookings?booking=${encodeURIComponent(id)}` : '/bookings';
      }
    }
    return '/bookings';
  }

  let link = notif?.link;
  if (!link || typeof link !== 'string') return '/bookings';

  // Strip custom schemes: kopartner://bookings/x → /bookings?booking=x
  link = link.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '/');

  if (link.startsWith('http://') || link.startsWith('https://')) {
    try {
      const u = new URL(link);
      link = u.pathname + u.search;
    } catch {
      return '/bookings';
    }
  }

  const bookingMatch = link.match(/^\/bookings\/([^/?#]+)/);
  if (bookingMatch) {
    return `/bookings?booking=${encodeURIComponent(bookingMatch[1])}`;
  }

  if (link.startsWith('/')) return link;
  return '/bookings';
}

const NotificationBell = () => {
  const { getAccessToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  // WebSocket connection for real-time notifications
  const { isConnected } = useWebSocket(getAccessToken(), {
    onNotification: useCallback((data) => {
      // New notification received via WebSocket
      setUnreadCount((c) => c + 1);
      // Prepend to list if dropdown is open
      setNotifications((prev) => {
        const exists = prev.find((n) => n.id === data.id);
        if (exists) return prev;
        return [data, ...prev].slice(0, 10);
      });
    }, []),
    onBookingUpdate: useCallback((data) => {
      // Booking status changed — increment unread (notification will follow)
      // This is handled by the notification event already
    }, []),
    onPaymentUpdate: useCallback((data) => {
      // Payment status changed — could trigger a refresh
    }, []),
  });

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch unread count on mount (initial load)
  useEffect(() => {
    fetchUnreadCount();
    // Fallback polling every 60s (in case WebSocket disconnects)
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await authAxios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.data.unread_count);
    } catch (_) {}
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`${API}/notifications?page_size=10`);
      setNotifications(res.data.data.notifications);
      setUnreadCount(res.data.data.unread_count);
    } catch (_) {}
    setLoading(false);
  };

  const handleOpen = () => {
    setOpen(!open);
    if (!open) fetchNotifications();
  };

  const handleClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await authAxios.post(`${API}/notifications/${notif.id}/read`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch (_) {}
    }

    const target = resolveNotificationTarget(notif);
    setOpen(false);
    navigate(target);
  };

  const handleMarkAllRead = async () => {
    try {
      await authAxios.post(`${API}/notifications/read-all`);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) {}
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        className="relative p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-full transition"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {/* WebSocket connection indicator */}
        <span className={`absolute bottom-0 right-0 w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-gray-300'}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
              {isConnected ? (
                <Wifi size={12} className="text-green-500" />
              ) : (
                <WifiOff size={12} className="text-gray-400" />
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-purple-500" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                No notifications yet
              </div>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => handleClick(notif)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition ${
                    !notif.is_read ? 'bg-purple-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {!notif.is_read && (
                      <span className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 shrink-0" />
                    )}
                    <div className={`flex-1 min-w-0 ${notif.is_read ? 'ml-5' : ''}`}>
                      <p className="text-sm font-medium text-gray-900 truncate">{notif.title}</p>
                      {notif.body && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.body}</p>
                      )}
                      <p className="text-[10px] text-gray-400 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 text-center">
              <button
                onClick={() => { setOpen(false); navigate('/bookings'); }}
                className="text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
